import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Switch,
  Textarea,
} from '@nextui-org/react';
import { Swords, Save } from 'lucide-react';
import lobbyService, {
  CreateLobbyPayload,
  LOBBY_GAME_MODES,
  Lobby,
} from '../../services/lobbyService';

const ACCENT = '#3fced1';

const MAX_MEMBER_OPTIONS = [2, 3, 4, 5, 6, 8, 10, 12, 16, 20, 24, 32];

/** ms-Timestamp → Wert für <input type="datetime-local"> (lokale Zeit) */
function toDatetimeLocalValue(timestamp?: number | null): string {
  if (!timestamp || timestamp <= 0) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

interface LobbyFormModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Wenn gesetzt → Bearbeiten-Modus */
  lobby?: Lobby | null;
  /** Wird nach erfolgreichem Speichern mit der (aktualisierten) Lobby aufgerufen */
  onSaved: (lobby: Lobby) => void;
}

const LobbyFormModal: React.FC<LobbyFormModalProps> = ({
  isOpen,
  onOpenChange,
  lobby,
  onSaved,
}) => {
  const isEdit = Boolean(lobby?.id);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [gameMode, setGameMode] = useState('');
  const [platform, setPlatform] = useState('');
  const [maxMembers, setMaxMembers] = useState('8');
  const [isPublic, setIsPublic] = useState(true);
  const [scheduledAt, setScheduledAt] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Formular beim Öffnen (neu) befüllen bzw. zurücksetzen
  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSaving(false);
    if (lobby) {
      setName(lobby.name ?? '');
      setDescription(lobby.description ?? '');
      setGameMode(lobby.gameMode ?? '');
      setPlatform(lobby.platform ?? '');
      setMaxMembers(String(lobby.maxMembers ?? 8));
      setIsPublic(lobby.isPublic !== false);
      setScheduledAt(toDatetimeLocalValue(lobby.scheduledAt));
      setTagsInput(Array.isArray(lobby.tags) ? lobby.tags.join(', ') : '');
    } else {
      setName('');
      setDescription('');
      setGameMode('');
      setPlatform('');
      setMaxMembers('8');
      setIsPublic(true);
      setScheduledAt('');
      setTagsInput('');
    }
  }, [isOpen, lobby]);

  const nameValid = useMemo(() => {
    const trimmed = name.trim();
    return trimmed.length >= 3 && trimmed.length <= 60;
  }, [name]);

  const submit = async () => {
    const trimmedName = name.trim();
    if (!nameValid) {
      setError('Der Name muss zwischen 3 und 60 Zeichen lang sein.');
      return;
    }
    setSaving(true);
    setError(null);

    let scheduledMs: number | null = null;
    if (scheduledAt) {
      const parsed = new Date(scheduledAt).getTime();
      if (Number.isFinite(parsed)) scheduledMs = parsed;
    }

    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0)
      .slice(0, 10);

    const payload: CreateLobbyPayload = {
      name: trimmedName,
      description: description.trim() || null,
      gameMode: gameMode || null,
      platform: platform.trim() || null,
      maxMembers: Number(maxMembers) || 8,
      isPublic,
      scheduledAt: scheduledMs,
      tags: tags.length > 0 ? tags : null,
    };

    try {
      const saved = isEdit && lobby?.id
        ? await lobbyService.updateLobby(lobby.id, payload)
        : await lobbyService.createLobby(payload);
      if (saved) {
        onSaved(saved);
        onOpenChange(false);
      } else {
        setError('Die Lobby konnte nicht gespeichert werden.');
      }
    } catch (err: any) {
      setError(
        lobbyService.extractErrorMessage(err, 'Die Lobby konnte nicht gespeichert werden.')
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      backdrop="blur"
      size="lg"
      scrollBehavior="inside"
      classNames={{
        base: 'bg-zinc-900 border border-zinc-800',
        header: 'text-white',
        body: 'text-gray-300',
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex items-center gap-2">
              <Swords size={18} style={{ color: ACCENT }} />
              {isEdit ? 'Lobby bearbeiten' : 'Neue Spiel-Lobby'}
            </ModalHeader>
            <ModalBody className="flex flex-col gap-4">
              <Input
                label="Name"
                placeholder="z.B. Lockeres 501 am Abend"
                value={name}
                onValueChange={(v) => {
                  setName(v);
                  setError(null);
                }}
                maxLength={60}
                isRequired
                description={`${name.trim().length} / 60 Zeichen (min. 3)`}
                classNames={{ inputWrapper: 'bg-zinc-950 border border-zinc-800' }}
              />
              <Textarea
                label="Beschreibung (optional)"
                placeholder="Worum geht es? Wer ist willkommen?"
                value={description}
                onValueChange={setDescription}
                maxLength={500}
                minRows={2}
                classNames={{ inputWrapper: 'bg-zinc-950 border border-zinc-800' }}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Select
                  label="Spielmodus"
                  placeholder="Modus wählen"
                  selectedKeys={gameMode ? [gameMode] : []}
                  onSelectionChange={(keys) => {
                    const key = Array.from(keys)[0];
                    setGameMode(key ? String(key) : '');
                  }}
                  classNames={{ trigger: 'bg-zinc-950 border border-zinc-800' }}
                >
                  {LOBBY_GAME_MODES.map((mode) => (
                    <SelectItem key={mode}>{mode}</SelectItem>
                  ))}
                </Select>
                <Input
                  label="Plattform (optional)"
                  placeholder="z.B. Scolia, Autodarts, vor Ort"
                  value={platform}
                  onValueChange={setPlatform}
                  maxLength={60}
                  classNames={{ inputWrapper: 'bg-zinc-950 border border-zinc-800' }}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Select
                  label="Max. Mitglieder"
                  selectedKeys={[maxMembers]}
                  onSelectionChange={(keys) => {
                    const key = Array.from(keys)[0];
                    if (key) setMaxMembers(String(key));
                  }}
                  classNames={{ trigger: 'bg-zinc-950 border border-zinc-800' }}
                >
                  {MAX_MEMBER_OPTIONS.map((n) => (
                    <SelectItem key={String(n)}>{`${n} Spieler`}</SelectItem>
                  ))}
                </Select>
                <Input
                  type="datetime-local"
                  label="Geplante Zeit (optional)"
                  value={scheduledAt}
                  onValueChange={setScheduledAt}
                  placeholder=" "
                  classNames={{ inputWrapper: 'bg-zinc-950 border border-zinc-800' }}
                />
              </div>
              <Input
                label="Tags (optional, mit Komma trennen)"
                placeholder="z.B. Anfänger, entspannt, Steeldart"
                value={tagsInput}
                onValueChange={setTagsInput}
                maxLength={200}
                classNames={{ inputWrapper: 'bg-zinc-950 border border-zinc-800' }}
              />
              <div className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-xl p-3">
                <div>
                  <p className="text-sm text-white">Öffentliche Lobby</p>
                  <p className="text-xs text-gray-500">
                    Öffentliche Lobbies sind für alle sichtbar.
                  </p>
                </div>
                <Switch
                  isSelected={isPublic}
                  onValueChange={setIsPublic}
                  size="sm"
                  aria-label="Öffentliche Lobby"
                />
              </div>
              {error && <p className="text-sm text-red-400">{error}</p>}
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose} className="text-gray-300">
                Abbrechen
              </Button>
              <Button
                className="font-semibold text-zinc-950"
                style={{ backgroundColor: ACCENT }}
                startContent={<Save size={16} />}
                isLoading={saving}
                isDisabled={!nameValid}
                onPress={submit}
              >
                {isEdit ? 'Speichern' : 'Lobby erstellen'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default LobbyFormModal;
